'use client';
import React from 'react';
import { DateTime } from 'luxon';
import cn from 'classnames';
import styled from '@benzinga/themetron';

export type PostDateType = 'relative' | 'short' | 'med' | 'full';
export type PostDateLayout = 'primary' | 'secondary';

interface PostElapsedProps {
  created: string;
  className?: string;
  dateType?: PostDateType;
  layout?: PostDateLayout;
  showTime?: boolean;
  locale?: string;
}

export const PostElapsed: React.FC<PostElapsedProps> = ({
  className,
  created,
  dateType = 'relative',
  layout = 'primary',
  locale = 'en',
  showTime = true,
}) => {
  const [postElapsed, setPostElapsed] = React.useState<string | null>(() => {
    if (!dateType) {
      const date = new Date(created);
      const relativeDate = DateTime.fromJSDate(date).setLocale(locale).toRelative();
      return relativeDate;
    }
    return null;
  });

  React.useEffect(() => {
    const date = new Date(created);
    if (dateType === 'full' && showTime) {
      setPostElapsed(DateTime.fromJSDate(date).setLocale(locale).toLocaleString(DateTime.DATETIME_FULL));
    } else if (dateType === 'med' && showTime) {
      setPostElapsed(DateTime.fromJSDate(date).setLocale(locale).toLocaleString(DateTime.DATETIME_MED));
    } else if (dateType === 'med') {
      setPostElapsed(DateTime.fromJSDate(date).setLocale(locale).toLocaleString(DateTime.DATE_MED));
    } else if (dateType === 'short' && showTime) {
      setPostElapsed(DateTime.fromJSDate(date).setLocale(locale).toLocaleString(DateTime.DATETIME_SHORT));
    } else {
      setPostElapsed(DateTime.fromJSDate(date).setLocale(locale).toRelative());
    }
  }, [created, dateType, showTime, locale]);

  if (!postElapsed) {
    return <StyledPlaceholder />;
  }

  return (
    <PostElapsedWrapper>
      {layout === 'secondary' && <Icon></Icon>}
      <StyledText className={cn('post-elapsed', { [`${className}`]: !!className })}>{postElapsed}</StyledText>
    </PostElapsedWrapper>
  );
};

const Icon: React.FC = () => {
  return (
    <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5 8H15M5 8V13.8668C5 14.6135 5 14.9867 5.13624 15.2719C5.25608 15.5228 5.44717 15.727 5.68237 15.8548C5.9495 16 6.29937 16 6.99807 16H13.0019C13.7006 16 14.05 16 14.3171 15.8548C14.5523 15.727 14.744 15.5228 14.8639 15.2719C15 14.987 15 14.6143 15 13.8691V8M5 8V7.4668C5 6.72006 5 6.34641 5.13624 6.0612C5.25608 5.81032 5.44717 5.60649 5.68237 5.47866C5.94976 5.33333 6.30006 5.33333 7.00012 5.33333H7.5M15 8V7.46461C15 6.71933 15 6.34613 14.8639 6.0612C14.744 5.81032 14.5523 5.60649 14.3171 5.47866C14.0497 5.33333 13.7002 5.33333 13.0001 5.33333H12.5M12.5 4V5.33333M12.5 5.33333H7.5M7.5 4V5.33333"
        stroke="#B8CBE6"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
};

const StyledText = styled.span`
  font-size: 0.75rem;
  padding: 3px 0;
  min-height: 18px;
`;

const StyledPlaceholder = styled.div`
  height: 22px;
  width: 80px;
`;

const PostElapsedWrapper = styled.div`
  align-items: center;
  display: inline-flex;
`;
